/**
 * Pure deal-overlay model for the Hierarchy tab: filter matching, per-account summaries,
 * subtree roll-ups, filter options and the selection trace. Shared so the client and tests agree
 * on one definition; every function takes the raw `deal_buckets` and filter state and derives,
 * so toggling a filter never refetches.
 */
import {
  DEAL_STATUSES, type DealBucket, type DealStatus, type HierarchyDeal,
} from './account-hierarchy-deals';
import type { HierarchyTree } from './account-hierarchy-tree';
import { fiscalRange, inFiscalRange, type FiscalPreset } from './fiscal-period';

/** Filter-selection keys for "no record type" / "no deal type" (the DB value is null). */
export const NO_PIPELINE_KEY = '__no_pipeline__';
export const NO_TYPE_KEY = '__no_type__';

export interface DealFilters {
  /**
   * Selected statuses. EMPTY means "everything except disqualified": Disqualified is noisy (its
   * amounts dwarf real revenue), so it only counts when explicitly selected.
   */
  statuses: DealStatus[];
  dateRange: FiscalPreset;
  /** 'all' or the selected pipeline names / `NO_PIPELINE_KEY`. */
  pipelines: 'all' | string[];
  /** 'all' or the selected deal types / `NO_TYPE_KEY`. */
  dealTypes: 'all' | string[];
}

/** When the overlay is turned on: open deals, any date, any pipeline, any type. */
export const DEFAULT_DEAL_FILTERS: DealFilters = { statuses: ['open'], dateRange: 'all', pipelines: 'all', dealTypes: 'all' };

export function effectiveStatuses(filters: Pick<DealFilters, 'statuses'>): DealStatus[] {
  return filters.statuses.length > 0 ? filters.statuses : DEAL_STATUSES.filter(s => s !== 'disqualified');
}

interface DealFields {
  status: DealStatus;
  pipeline: string | null;
  deal_type: string | null;
  close_year: number | null;
  close_month: number | null;
}

function selected(selection: 'all' | string[], value: string | null, emptyKey: string): boolean {
  return selection === 'all' || selection.includes(value ?? emptyKey);
}

/** Does one bucket (or one deal) pass every filter? Date range applies to close date for every status. */
export function dealMatches(fields: DealFields, filters: DealFilters, now: Date): boolean {
  return effectiveStatuses(filters).includes(fields.status)
    && inFiscalRange(fiscalRange(filters.dateRange, now), fields.close_year, fields.close_month)
    && selected(filters.pipelines, fields.pipeline, NO_PIPELINE_KEY)
    && selected(filters.dealTypes, fields.deal_type, NO_TYPE_KEY);
}

export const bucketMatches = (bucket: DealBucket, filters: DealFilters, now: Date): boolean => dealMatches(bucket, filters, now);
export const hierarchyDealMatches = (deal: HierarchyDeal, filters: DealFilters, now: Date): boolean => dealMatches(deal, filters, now);

export interface PrimaryDeal {
  status: DealStatus;
  id: string;
  name: string | null;
  amount: number | null;
  arr: number | null;
}

export interface AccountDealSummary {
  count: number;
  amount: number;
  arr: number;
  counts: Record<DealStatus, number>;
  /** The deal the card names: open > won > lost > disqualified, then the larger amount. */
  primary: PrimaryDeal | null;
  /** Matching deals beyond the primary one. */
  moreCount: number;
}

const STATUS_PRIORITY: Record<DealStatus, number> = { open: 0, won: 1, lost: 2, disqualified: 3 };

function betterPrimary(a: PrimaryDeal, b: PrimaryDeal | null): boolean {
  if (!b) return true;
  if (STATUS_PRIORITY[a.status] !== STATUS_PRIORITY[b.status]) return STATUS_PRIORITY[a.status] < STATUS_PRIORITY[b.status];
  // Same status: the larger amount wins; any amount beats none (the SQL orders `amount DESC NULLS LAST`).
  if (a.amount === null || b.amount === null) return a.amount !== null && b.amount === null;
  return a.amount > b.amount;
}

/** Per-account summary of the buckets that pass the filters; accounts with no match are absent. */
export function summarizeDeals(buckets: readonly DealBucket[], filters: DealFilters, now: Date): Map<string, AccountDealSummary> {
  const out = new Map<string, AccountDealSummary>();
  for (const b of buckets) {
    if (!bucketMatches(b, filters, now)) continue;
    let s = out.get(b.account_id);
    if (!s) {
      s = { count: 0, amount: 0, arr: 0, counts: { open: 0, won: 0, lost: 0, disqualified: 0 }, primary: null, moreCount: 0 };
      out.set(b.account_id, s);
    }
    s.count += b.deal_count;
    s.amount += b.amount_sum;
    s.arr += b.arr_sum;
    s.counts[b.status] += b.deal_count;
    if (b.top_deal) {
      const candidate: PrimaryDeal = { status: b.status, ...b.top_deal };
      if (betterPrimary(candidate, s.primary)) s.primary = candidate;
    }
  }
  for (const s of out.values()) s.moreCount = s.primary ? s.count - 1 : s.count;
  return out;
}

export interface DealRollup {
  count: number;
  amount: number;
  arr: number;
}

/** Inclusive roll-up per account: itself plus every descendant (the whole tree, shown or not). */
export function rollupDeals(tree: HierarchyTree, summaries: ReadonlyMap<string, AccountDealSummary>): Map<string, DealRollup> {
  const out = new Map<string, DealRollup>();
  const visit = (id: string): DealRollup => {
    const own = summaries.get(id);
    const total: DealRollup = { count: own?.count ?? 0, amount: own?.amount ?? 0, arr: own?.arr ?? 0 };
    for (const child of tree.childrenOf.get(id) ?? []) {
      const r = visit(child.id);
      total.count += r.count;
      total.amount += r.amount;
      total.arr += r.arr;
    }
    out.set(id, total);
    return total;
  };
  if (tree.byId.has(tree.rootId)) visit(tree.rootId);
  return out;
}

export interface FilterOption {
  key: string;
  label: string;
}

export interface DealFilterOptions {
  pipelines: FilterOption[];
  dealTypes: FilterOption[];
}

function optionsFrom(values: Array<string | null>, emptyKey: string, emptyLabel: string): FilterOption[] {
  const named = [...new Set(values.filter((v): v is string => v !== null))].sort((a, b) => a.localeCompare(b));
  const options = named.map(v => ({ key: v, label: v }));
  if (values.includes(null)) options.push({ key: emptyKey, label: emptyLabel });
  return options;
}

/**
 * A retained selection can name options this hierarchy does not offer (filters survive J/K moves
 * between accounts). Drop those keys; a selection covering every CURRENT option is 'all'.
 */
export function pruneSelection(selection: 'all' | string[], options: readonly FilterOption[]): 'all' | string[] {
  if (selection === 'all') return 'all';
  const offered = new Set(options.map(o => o.key));
  const kept = selection.filter(k => offered.has(k));
  return options.length > 0 && options.every(o => kept.includes(o.key)) ? 'all' : kept;
}

/** Toggle one key in a multi-select; "all selected" is membership against the current options, never a length compare. */
export function toggleSelection(selection: 'all' | string[], options: readonly FilterOption[], key: string): 'all' | string[] {
  const all = options.map(o => o.key);
  const current = selection === 'all' ? all : selection.filter(k => all.includes(k));
  const next = current.includes(key) ? current.filter(k => k !== key) : all.filter(k => current.includes(k) || k === key);
  return all.every(k => next.includes(k)) ? 'all' : next;
}

/** Pipeline / deal type options come from the data (record type names present), never a hardcoded list. */
export function dealFilterOptions(buckets: readonly DealBucket[]): DealFilterOptions {
  return {
    pipelines: optionsFrom(buckets.map(b => b.pipeline), NO_PIPELINE_KEY, 'No pipeline'),
    dealTypes: optionsFrom(buckets.map(b => b.deal_type), NO_TYPE_KEY, 'No type'),
  };
}

/** The selection's lineage: ancestors + itself + every descendant (never siblings). */
export function traceIds(tree: HierarchyTree, selectedId: string): Set<string> {
  const out = new Set<string>([selectedId]);
  for (const a of tree.ancestorsOf(selectedId)) out.add(a);
  const stack = [selectedId];
  while (stack.length) {
    const id = stack.pop()!;
    for (const c of tree.childrenOf.get(id) ?? []) {
      if (out.has(c.id)) continue;
      out.add(c.id);
      stack.push(c.id);
    }
  }
  return out;
}

export const DEAL_STATUS_LABEL: Record<DealStatus, string> = {
  open: 'Open', won: 'Won', lost: 'Lost', disqualified: 'Disqualified',
};

/** "2 open" — the chip text for a status with `count` deals. */
export function statusChipsLabel(status: DealStatus, count: number): string {
  return `${count} ${DEAL_STATUS_LABEL[status].toLowerCase()}`;
}

export interface WonByType {
  dealType: string | null;
  count: number;
  amount: number;
  arr: number;
}

/**
 * The account's WON deals split by deal type (largest amount first), so a $900K Flat Renewal
 * never reads as $900K of new business. Other statuses are ignored.
 */
export function wonByDealType(deals: readonly HierarchyDeal[]): WonByType[] {
  const byType = new Map<string | null, WonByType>();
  for (const d of deals) {
    if (d.status !== 'won') continue;
    const row = byType.get(d.deal_type) ?? { dealType: d.deal_type, count: 0, amount: 0, arr: 0 };
    row.count += 1;
    row.amount += d.amount ?? 0;
    row.arr += d.arr ?? 0;
    byType.set(d.deal_type, row);
  }
  return [...byType.values()].sort((a, b) => b.amount - a.amount);
}
